import { clsx, type ClassValue } from "clsx";

/** Concatena clases condicionalmente. */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

const FORMATO_COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

export function formatearCop(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return "—";
  return FORMATO_COP.format(valor);
}

export function formatearKwh(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return "—";
  return `${valor.toLocaleString("es-CO", { maximumFractionDigits: 2 })} kWh`;
}

export function formatearFechaCorta(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Solo fecha — útil para columnas de tabla donde la hora es ruido
 *  (radicación, vencimiento). Formato: "02 jun 2026". */
export function formatearFechaSolo(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Forma del error que tanto axios como nuestro `fetch` wrapper pueden
 * lanzar. `response.data.detail` es la convención de FastAPI para
 * mensajes legibles; `message` es la del `Error` estándar.
 */
export interface ApiError {
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
    };
  };
  message?: unknown;
}

/** Type guard tolerante para reconocer un `ApiError` sin aserciones inseguras. */
export function isApiError(value: unknown): value is ApiError {
  return value !== null && typeof value === "object";
}

/**
 * Extrae el mensaje legible de un error capturado por axios/fetch.
 *
 * Maneja la cadena: AxiosError (con `response.data.detail` del backend
 * FastAPI) → `Error` estándar → string → fallback.
 */
export function extractApiError(
  err: unknown,
  fallback: string = "Error de red",
): string {
  if (typeof err === "string") return err;
  if (isApiError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (typeof err.message === "string" && err.message.trim()) {
      return err.message;
    }
  }
  return fallback;
}

export function decodificarJwt(token: string): {
  sub?: string;
  rol?: string;
  exp?: number;
} | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}
